import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Card, CardContent, CardMedia, CircularProgress, Container } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import { db } from '@/firebase/firebase';
import { logger } from '@/services/loggerService';
import { RouteMap } from '@/routes/Routes';
import { toCampaignDoc, type CampaignDoc } from './types';

/**
 * Public feed of published What's New posts, newest first.
 * Reads `campaigns` from Firestore directly (public read).
 * A doc is shown only if `active === true`, `slug` is set, and `publishedAt` is set.
 */
const WhatsNewFeed = () => {
  const [posts, setPosts] = useState<CampaignDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      try {
        const q = query(collection(db, 'campaigns'), where('active', '==', true));
        const snapshot = await getDocs(q);
        if (cancelled) return;

        const docs = snapshot.docs
          .map((doc) => toCampaignDoc(doc.id, doc.data()))
          .filter((c) => c.slug && c.publishedAt)
          .sort((a, b) => (b.publishedAt?.toMillis() ?? 0) - (a.publishedAt?.toMillis() ?? 0));

        setPosts(docs);
      } catch (err) {
        if (cancelled) return;
        logger.warn('firebase', "Failed to load What's New feed", err);
        setError('Could not load posts. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>What's New — NoteGo</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight={700} mb={1}>
          What's New
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Latest updates from the NoteGo team.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Typography color="error">{error}</Typography>}

        {!loading && !error && posts.length === 0 && (
          <Typography color="text.secondary">No posts yet — check back soon.</Typography>
        )}

        {!loading &&
          !error &&
          posts.map((post) => (
            <Card
              key={post.id}
              component={RouterLink}
              to={`${RouteMap.WHATS_NEW}/${post.slug}`}
              sx={{
                mb: 3,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                '&:hover': { boxShadow: 4 },
                transition: 'box-shadow 0.2s',
              }}
            >
              {post.heroImageUrl && (
                <CardMedia
                  component="img"
                  image={post.heroImageUrl}
                  alt={post.title}
                  sx={{ width: { xs: '100%', sm: 200 }, objectFit: 'cover', flexShrink: 0 }}
                />
              )}
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {post.title}
                </Typography>
                {post.excerpt && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {post.excerpt}
                  </Typography>
                )}
                <Typography variant="body2" component="div" color="text.secondary" mt={1}>
                  {post.publishedAt
                    ? new Date(post.publishedAt.toMillis()).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : ''}
                </Typography>
              </CardContent>
            </Card>
          ))}
      </Container>
    </>
  );
};

export default WhatsNewFeed;
